import React, { useCallback, useMemo, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import heic2any from 'heic2any';
import axios from "axios";

// Define styles for the dropzone component
const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    minHeight: '100px',
    maxWidth: 'none',
    marginLeft: 'auto',
    marginRight: 'auto',
    borderWidth: 2,
    borderRadius: 2,
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#000000',
    outline: 'none',
    transition: 'border .24s ease-in-out',
    maxWidth: '800px',
};

const focusedStyle = {
    borderColor: '#2196f3',
};

const acceptStyle = {
    borderColor: '#00e676',
};

const rejectStyle = {
    borderColor: '#ff1744',
};

const ImageDrop = ({ imagePreview }) => {
    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(false);

    // Memoized + revoked: createObjectURL in the render body mints a new blob
    // URL on every render and never frees it.
    const previewUrl = useMemo(() => (image ? URL.createObjectURL(image) : null), [image]);

    useEffect(() => {
        if (previewUrl) {
            return () => URL.revokeObjectURL(previewUrl);
        }
    }, [previewUrl]);

    const handleImageDrop = (file) => {
        setLoading(true);
        setImage(file);
        onSearchSubmit(file);
    };

    const onSearchSubmit = (file) => {
        const formData = new FormData();
        formData.append("file", file);
        setLoading(true);
        axios.post("/api/upload-image", formData, {
            headers: {
                "X-CSRF-TOKEN": csrf_token, // Ensure csrf_token is available
            },
        })
            .then((response) => {
                if (response.data && response.data.token) {
                    setImage(null);
                    setLoading(false);
                    const urlToOpen = '/ai-search/' + encodeURIComponent(response.data.token);
                    window.location.href = urlToOpen;
                } else {
                    // No token means the server didn't treat this as a share
                    // (e.g. the referer was stripped by Referrer-Policy) —
                    // don't leave the spinner running forever.
                    setLoading(false);
                    alert("Couldn't start the image search — please try again.");
                }
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
                // Surface throttle/validation/unavailable errors instead of a
                // spinner that silently dies.
                alert(
                    (error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
                    "Something went wrong with the image search — please try again."
                );
            });
    };

    const onDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];

        // react-dropzone still fires onDrop with an empty accepted list when
        // every dropped file was rejected.
        if (!file) {
            return;
        }

        if (file.size > 25 * 1024 * 1024) {
            alert("Image is too large (max 25MB). Please use a smaller image.");
            return;
        }

        if (file.type === 'image/heic') {
            // Busy from the START of conversion: otherwise the dropzone stays
            // interactive while heic2any works, so a second drop races the
            // first (two uploads, or this catch killing the other's spinner).
            setLoading(true);

            heic2any({
                blob: file,
                toType: "image/jpeg",
                quality: 0.5
            })
                .then((jpegBlob) => {
                    const convertedFile = new File([jpegBlob], file.name.split('.').slice(0, -1).join('.') + ".jpeg", { type: "image/jpeg" });
                    handleImageDrop(convertedFile);
                })
                .catch((e) => {
                    console.log(e);
                    setLoading(false);
                    alert("Couldn't process this HEIC image — please try a JPEG or PNG.");
                })
        } else {
            handleImageDrop(file);
        }
    }, [handleImageDrop]);

    useEffect(() => {
        return () => {
            // Cleanup function to cancel any ongoing tasks or subscriptions
            // if needed. Add cleanup code here if necessary.
        };
    }, []);

    const onDropRejected = useCallback((fileRejections) => {
        // Handle the rejected file here, e.g. by displaying an error message
        alert("Only JPEG, PNG, and HEIC files are allowed");
    }, []);

    // Get the props and styles from the useDropzone hook
    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        accept: {
            'image/jpeg': [],
            'image/png': [],
            'image/heic': [],
        }
    });

    // Memoized style object for the dropzone component
    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject, isDragActive]
    );

    return (
        <>
            {loading ? (
                <div className="row w-100 align-items-center justify-content-center">
                    <div>
                        <div className="position-relative">
                            {previewUrl && (
                                <img className="object-fit" width="150" height="150" style={{ opacity: '.5' }} src={previewUrl} alt="Preview" />
                            )}

                            <div className="spinner-grow text-primary position-absolute" role="status"> <span className="sr-only">searching...</span></div>

                        </div>
                        {loading && (
                            <p className="text-center">Searching...</p>
                        )}
                    </div>
                </div>
            ) :
                <div className={'align-items-center justify-content-center h-100' + (imagePreview ? " d-none" : " d-flex")} {...getRootProps({ style })}>
                    {!loading &&
                        <>
                            <input {...getInputProps()} />
                            {isDragActive ? (
                                <p>Drop the files here ...</p>
                            ) : (
                                <>
                                    <p className="font-weight-bold">Search a Location Using An Image As Reference</p>
                                    <small>Drag & Drop an image here or click here to select a file</small>
                                </>
                            )}
                        </>
                    }
                </div>
            }
        </>
    );
};

export default ImageDrop;
